import { z } from 'zod';
import { FilterNodeSchema } from './query/ast';
import { TicketListItemSchema } from './tickets';

/** Counts are exact up to this value; above it `{ count: COUNT_CAP, exact: false }` (ADR-0015 §5). */
export const COUNT_CAP = 10_000;

export const capCount = (n: number): { count: number; exact: boolean } =>
  n > COUNT_CAP ? { count: COUNT_CAP, exact: false } : { count: n, exact: true };

/** `POST /tickets/search` without `groupBy` (S-06-01). */
export const SearchResponseSchema = z.object({
  items: z.array(TicketListItemSchema),
  nextCursor: z.string().nullable(),
});
export type SearchResponse = z.infer<typeof SearchResponseSchema>;

/**
 * One group of a grouped search: `key` is the grouped value (id, category, `'true'`) or null
 * for the "no value" bucket; `nextCursor` pages inside the group by re-running the query
 * with `groupBy` removed and `{ field, op: 'eq', value: key }` added to the filter.
 */
export const SearchGroupSchema = z.object({
  key: z.string().nullable(),
  count: z.int().min(0).max(COUNT_CAP),
  exact: z.boolean(),
  items: z.array(TicketListItemSchema),
  nextCursor: z.string().nullable(),
});
export type SearchGroup = z.infer<typeof SearchGroupSchema>;

/** `POST /tickets/search` with `groupBy` (S-06-05). */
export const GroupedSearchResponseSchema = z.object({
  groups: z.array(SearchGroupSchema),
  nextCursor: z.null(),
});
export type GroupedSearchResponse = z.infer<typeof GroupedSearchResponseSchema>;

export const SearchResultSchema = z.union([GroupedSearchResponseSchema, SearchResponseSchema]);
export type SearchResult = z.infer<typeof SearchResultSchema>;

/** `POST /tickets/count` (S-06-06). */
export const CountRequestSchema = z.strictObject({
  filter: FilterNodeSchema.optional(),
});
export type CountRequest = z.infer<typeof CountRequestSchema>;

export const CountResponseSchema = z.object({
  count: z.int().min(0).max(COUNT_CAP),
  exact: z.boolean(),
});
export type CountResponse = z.infer<typeof CountResponseSchema>;

/** `POST /views/{id}/run`: the stored AST with the caller's `$me` and an optional cursor. */
export const RunViewSchema = z.strictObject({
  cursor: z.string().max(4096).optional(),
});
export type RunView = z.infer<typeof RunViewSchema>;
